import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Settings2, Save, Loader2, ImageIcon } from "lucide-react";

const CURRENCIES = ["USD", "EUR", "GBP", "AED", "JPY", "CAD", "AUD", "BRL"];

export default function Settings() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    brand_name: user?.brand_name || "KARYOW",
    logo_url: user?.logo_url || "",
    currency: user?.currency || "USD",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/settings/brand", form);
      setUser(res.data);
      toast.success("Workspace updated");
    } catch (e) { toast.error("Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl">
      <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Settings2 className="h-4 w-4 text-lime-400" />
          <h3 className="font-display font-semibold text-white">Workspace & Brand</h3>
        </div>

        <div className="flex items-center gap-4 mb-6">
          {form.logo_url ? (
            <img src={form.logo_url} alt="logo" className="h-16 w-16 rounded-xl object-cover border border-[#242B30]" onError={(e) => (e.target.style.display = "none")} />
          ) : (
            <img src="/karyow-logo.png" alt="KARYOW" className="h-16 w-16 rounded-xl object-cover border border-[#242B30]" />
          )}
          <div>
            <div className="font-display text-lg font-bold text-white">{form.brand_name || "KARYOW"}</div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-lime-400 uppercase">AI Ads Manager</div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Brand Name</Label>
            <Input data-testid="brand-name-input" value={form.brand_name}
              onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
              className="mt-1.5 bg-[#0F1315] border-[#242B30]" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Logo URL</Label>
            <Input data-testid="logo-url-input" value={form.logo_url} placeholder="https://…/logo.png"
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono text-sm" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Reporting Currency</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="currency-select" className="mt-1.5 bg-[#0F1315] border-[#242B30] w-full sm:w-40 min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <button data-testid="save-settings-btn" onClick={save} disabled={saving}
          className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-6 min-h-[44px] text-sm font-semibold text-[#0A0D0E] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </button>
      </div>

      <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-6">
        <h3 className="font-display font-semibold text-white mb-2">Account</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div><div className="text-[10px] font-mono uppercase text-zinc-500">Name</div><div className="text-zinc-200 break-words">{user?.name}</div></div>
          <div className="min-w-0"><div className="text-[10px] font-mono uppercase text-zinc-500">Email</div><div className="text-zinc-200 font-mono break-all">{user?.email}</div></div>
        </div>
      </div>
    </div>
  );
}
